import { Box, Chip, CircularProgress, Divider, Stack, Tab, Tabs, Typography, useTheme } from "@mui/material";
import axios from "axios";
import { format } from "date-fns";
import { useEffect, useState } from "react";
import { API_SERVICE_BACKEND } from "src/config";
import ConversationTranscript from "./Transcript";
import Overview from "./Overview";
import Metadata from "./Metadata";
import { ANALYTICS_TYPE } from "src/helpers/constants";

const AnalyticsTabs = [
  {
    label: "Metadata",
    value: "metadata",
  },
  {
    label: "Overview",
    value: "overview",
  },
  {
    label: "Transcript",
    value: "transcript",
  },
];

const PhoneCallSequenceStep = ({ template, providers, content, analyticsType }) => {
  const theme = useTheme();
  const [callData, setCallData] = useState({});
  const [isFetching, setIsFetching] = useState(false);
  const [currentTab, setCurrentTab] = useState("metadata");

  useEffect(() => {
    const fetchEmailContent = async () => {
      try {
        setIsFetching(true);
        if (analyticsType === ANALYTICS_TYPE.INBOUND_CALL) {
          const res = await axios.post(
            `${API_SERVICE_BACKEND}/inbound-conversation-data`,
            { callSid: content }
          );
          if (!res.data) return;
          let conversationData = {
            ...res.data.data,
            caller: res.data?.data[0]?.caller,
            callerCountry: res.data?.data[0]?.callerCountry,
            calledAt: res.data?.data[0]?.calledAt,
            calledCountry: res.data?.data[0]?.calledCountry,
            called: res.data?.data[0]?.called,
            callStatus: res.data?.data[0]?.status,
            callDuration: res.data?.data[0]?.callDuration,
            fallbackConversation: res.data?.data[0]?.fallbackConversationData,
            conversationId: res.data?.data[0]?.conversationData?.conversation_initiation_client_data?.dynamic_variables?.system__conversation_id,
            transcript: res.data?.data[0]?.conversationData?.transcript,
            agent: res.data?.data[0]?.agent,
            customer: res.data?.data[0]?.customer,
            startTime: res.data?.data[0]?.startTime,
            endTime: res.data?.data[0]?.endTime,
            duration: res.data?.data[0]?.duration,
            analysis: res.data?.data[0]?.conversationData?.analysis,
          };
          setCallData(conversationData);
        } else {
          const res = await axios.get(
            `${API_SERVICE_BACKEND}/getEmailSentInfo?id=${content}&analytics=true`
          );

          if (!res.data) return;
          setCallData(res.data.multiChannel.outbound_call);
        }
      } catch (error) {
        //console.log(error);
      } finally {
        setIsFetching(false);
      }
    };
    if (content) {
      fetchEmailContent();
    }
  }, [content]);

  const handleChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  return (
    <>
      <Stack
        sx={{
          backgroundColor: "#FFFFFF",
          borderRadius: "12px",
          display: "flex",
          flexDirection: "column",
        }}
        className="glass-pane"
      >
        <Tabs
          value={currentTab}
          onChange={handleChange}
          variant="scrollable"
          scrollButtons="auto"
          aria-label="scrollable auto tabs example"
          sx={{
            borderBottom: "1px solid rgba(224, 224, 229, 0.5)",
            "& .MuiTab-root": {
              m: 0.8,
              mr: 0,
              minHeight: "36px",
              p: 1,
              color: theme.palette.text.primary,
              "&.Mui-selected": {},
            },
          }}
        >
          {AnalyticsTabs.map((tab) => (
            <Tab
              disabled={isFetching}
              key={tab.value}
              label={tab.label}
              value={tab.value}
              sx={{
                "&.Mui-selected": {
                  fontWeight: 600,
                },
              }}
            />
          ))}
          {callData?.fallbackConversation && (
            <Tab
              disabled={isFetching}
              label="Fallback Conversation Overview"
              value="fallbackOverview"
              sx={{
                "&.Mui-selected": {
                  fontWeight: 600,
                },
              }}
            />
          )}
          {callData?.fallbackConversation && (
            <Tab
              disabled={isFetching}
              label="Fallback Conversation Transcript"
              value="fallback"
              sx={{
                "&.Mui-selected": {
                  fontWeight: 600,
                },
              }}
            />
          )}
        </Tabs>
        {isFetching && (
          <Box
            sx={{
              padding: "20px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
              height: "100%",
            }}
          >
            <CircularProgress size={20} />
          </Box>
        )}
        {!isFetching && !callData && (
          <Box
            sx={{
              padding: "20px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
              height: "100%",
            }}
          >
            <Typography variant="body1">No data available</Typography>
          </Box>
        )}
        {!isFetching && callData && (
          <Box
            sx={{
              paddingX: "10px",
              paddingY: "20px",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {currentTab === "metadata" && (
              <Box>
                <Metadata data={callData} />
              </Box>
            )}
            {currentTab === "overview" && (
              <Box>
                <Overview data={callData} />
              </Box>
            )}
            {currentTab === "transcript" && (
              <Box>
                <ConversationTranscript transcript={callData.transcript} conversationId={callData.conversationId} />
              </Box>
            )}
            {currentTab === "fallbackOverview" && (
              <Box>
                <Overview data={callData.fallbackConversation} />
              </Box>
            )}
            {currentTab === "fallback" && (
              <Box>
                <ConversationTranscript
                  transcript={callData.fallbackConversation.transcript}
                  conversationId={callData.fallbackConversation.conversationId}
                />
              </Box>
            )}
          </Box>
        )}
      </Stack>
    </>
  );
};

export default PhoneCallSequenceStep;
